/**
 * MiEstadoCurso: panel que muestra al estudiante su avance de asistencia
 * respecto del curso de Capacitaciones vinculado al espacio del Campus.
 *
 * Se renderiza al tope de la vista de Exámenes para que sepan cuánto les falta
 * para desbloquear los exámenes.
 *
 * Se oculta automáticamente si:
 *  - el espacio no tiene curso vinculado, o
 *  - el usuario es staff (coordinador/docente), o
 *  - el curso vinculado no tiene jornadas configuradas.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Award, GraduationCap, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MiEstadoCurso({ spaceId, esStaff }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (esStaff) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/campus/espacios/${spaceId}/mi-estado-curso`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setData(r.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [spaceId, esStaff]);

  if (esStaff) return null;
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Verificando tu estado del curso…
      </div>
    );
  }
  if (!data || !data.curso) return null;
  if (!data.total_jornadas) return null; // curso sin jornadas configuradas

  const {
    curso, inscripto, cancelada, acredita,
    jornadas_asistidas, total_jornadas, min_jornadas_acreditacion, jornadas,
  } = data;

  const faltantes = Math.max(0, min_jornadas_acreditacion - jornadas_asistidas);
  const pct = min_jornadas_acreditacion > 0
    ? Math.min(100, Math.round((jornadas_asistidas / min_jornadas_acreditacion) * 100))
    : 100;

  // Estado visual
  let cardClass, iconClass, IconComp, headline;
  if (!inscripto) {
    cardClass = 'border-gray-300 bg-gray-50';
    iconClass = 'text-gray-500';
    IconComp = AlertCircle;
    headline = `No estás inscripto/a en el curso "${curso.nombre}"`;
  } else if (cancelada) {
    cardClass = 'border-red-300 bg-red-50';
    iconClass = 'text-red-600';
    IconComp = XCircle;
    headline = 'Tu inscripción al curso está cancelada';
  } else if (acredita) {
    cardClass = 'border-emerald-300 bg-emerald-50';
    iconClass = 'text-emerald-600';
    IconComp = Award;
    headline = '¡Ya acreditaste la asistencia! Podés rendir los exámenes.';
  } else {
    cardClass = 'border-amber-300 bg-amber-50';
    iconClass = 'text-amber-600';
    IconComp = GraduationCap;
    headline = `Te ${faltantes === 1 ? 'falta' : 'faltan'} ${faltantes} jornada${faltantes !== 1 ? 's' : ''} para acreditar`;
  }

  return (
    <Card className={`border ${cardClass}`} data-testid="mi-estado-curso">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <IconComp className={`w-6 h-6 mt-0.5 flex-shrink-0 ${iconClass}`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">{headline}</p>
            <p className="text-xs text-gray-600 mt-0.5">
              Curso vinculado: <strong>{curso.nombre}</strong>
            </p>

            {inscripto && !cancelada && (
              <>
                {/* Progress bar */}
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>
                      Asistencia: <strong>{jornadas_asistidas}</strong> de <strong>{total_jornadas}</strong> jornadas
                      {min_jornadas_acreditacion !== total_jornadas && (
                        <span className="text-gray-400"> · mínimo: {min_jornadas_acreditacion}</span>
                      )}
                    </span>
                    <span className="tabular-nums font-medium">{pct}%</span>
                  </div>
                  <div className="w-full h-2 bg-white rounded-full overflow-hidden border border-gray-200">
                    <div
                      className={`h-full transition-all ${acredita ? 'bg-emerald-500' : 'bg-amber-400'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>

                {/* Lista de jornadas (solo si son varias) */}
                {jornadas.length > 1 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {jornadas.map((j, idx) => (
                      <span
                        key={j.id}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${
                          j.asistio
                            ? 'bg-emerald-100 border-emerald-300 text-emerald-800'
                            : 'bg-white border-gray-300 text-gray-500'
                        }`}
                        title={j.fecha || ''}
                        data-testid={`jornada-${idx}`}
                      >
                        {j.asistio ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {j.titulo || `Jornada ${idx + 1}`}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
